import React, { useEffect, useState, useRef } from 'react';
import StaffScanGate from '@/components/StaffScanGate';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Scan, Loader2, Package, Check, X, Truck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const ShirtsAssortment = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [jwId, setJwId] = useState(searchParams.get('jw_id') || '');
  const [boxes, setBoxes] = useState([]);
  const [activeBox, setActiveBox] = useState('');
  const [boxInput, setBoxInput] = useState('');
  const [usn, setUsn] = useState('');
  const [scanning, setScanning] = useState(false);
  const [creatingBox, setCreatingBox] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const usnRef = useRef(null);

  const loadOrders = () => apiClient.get('/shirt-mfg/jw-orders?status=COMPLETED').then(
    (r) => setOrders(r.data.orders || [])
  );
  const loadBoxes = (id) => {
    if (!id) return Promise.resolve();
    return apiClient.get(`/shirt-mfg/assortment/boxes?jw_id=${id}`).then(
      (r) => setBoxes(r.data.boxes || [])
    );
  };

  useEffect(() => { loadOrders(); }, []);
  useEffect(() => { if (jwId) loadBoxes(jwId); }, [jwId]);
  useEffect(() => { if (activeBox && usnRef.current) usnRef.current.focus(); }, [activeBox]);

  const createBox = async () => {
    if (!jwId || !boxInput.trim()) { toast.error('JW + box barcode required'); return; }
    setCreatingBox(true);
    try {
      await apiClient.post('/shirt-mfg/assortment/boxes', {
        jw_id: jwId, box_barcode: boxInput.trim(),
      });
      toast.success(`Box ${boxInput} opened`);
      setActiveBox(boxInput.trim());
      setBoxInput('');
      await loadBoxes(jwId);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setCreatingBox(false); }
  };

  const scan = async (e) => {
    e?.preventDefault?.();
    const u = usn.trim();
    if (!u || !activeBox) return;
    setScanning(true);
    try {
      await apiClient.post('/shirt-mfg/assortment/scan-usn', {
        box_barcode: activeBox, usn_code: u,
      });
      toast.success(`${u} boxed`);
      setUsn('');
      await loadBoxes(jwId);
    } catch (err) { toast.error(err.response?.data?.detail || 'Scan failed'); }
    finally { setScanning(false); }
  };

  const closeBox = async (barcode) => {
    try {
      await apiClient.post(`/shirt-mfg/assortment/boxes/${barcode}/close`);
      toast.success(`Box ${barcode} closed (READY_FOR_INTERNAL_TRANSFER)`);
      if (activeBox === barcode) setActiveBox('');
      await loadBoxes(jwId);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const transferAll = async () => {
    const ready = boxes.filter((b) => b.status === 'READY_FOR_INTERNAL_TRANSFER').map((b) => b.box_barcode);
    if (!ready.length) { toast.error('No ready boxes'); return; }
    setTransferring(true);
    try {
      const r = await apiClient.post('/shirt-mfg/assortment/transfer-to-sfg', {
        box_barcodes: ready,
      });
      toast.success(`Transferred ${r.data.transferred} box(es) to Dispatch`);
      await loadBoxes(jwId);
    } catch (err) { toast.error(err.response?.data?.detail || 'Transfer failed'); }
    finally { setTransferring(false); }
  };

  const activeBoxDoc = boxes.find((b) => b.box_barcode === activeBox);

  return (
    <StaffScanGate stationName="Shirt Mfg — Shirts Assortment">
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="shirt-mfg-assortment">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/sections/shirt-mfg/hub')}
              className="text-[#F9FAFB] hover:bg-white/10">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Shirts — Assortment Packing</h1>
              <p className="text-sm text-[#E8DCC8]">Box-up completed JW assemblies → transfer to SFG</p>
            </div>
          </div>
          <Button variant="secondary" onClick={transferAll} disabled={transferring}
            data-testid="transfer-all-btn">
            {transferring ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Truck className="w-4 h-4 mr-2" />}
            Transfer Ready Boxes to SFG
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Select JW Order</CardTitle>
          </CardHeader>
          <CardContent>
            <Select value={jwId} onValueChange={(v) => { setJwId(v); setActiveBox(''); }}>
              <SelectTrigger className="max-w-md" data-testid="jw-select-trigger">
                <SelectValue placeholder="Pick a completed JW order…" />
              </SelectTrigger>
              <SelectContent>
                {orders.length === 0 && <SelectItem disabled value="none">No completed JW orders</SelectItem>}
                {orders.map((o) => (
                  <SelectItem key={o.jw_id} value={o.jw_id}>
                    {o.jw_id} — {o.assembly_item} (recv {o.received_qty || 0})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {jwId && (
          <>
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Open a new box</CardTitle>
              </CardHeader>
              <CardContent className="flex gap-2 items-end">
                <div>
                  <Label className="text-xs">New box barcode</Label>
                  <Input value={boxInput} onChange={(e) => setBoxInput(e.target.value)}
                    className="w-[260px] mt-1" placeholder="SHM-BOX-001"
                    data-testid="new-box-input" />
                </div>
                <Button onClick={createBox} disabled={creatingBox}
                  className="bg-[#2D5F4F] hover:bg-[#234A3D]"
                  data-testid="open-box-btn">
                  {creatingBox ? <Loader2 className="w-4 h-4 animate-spin" /> : <Package className="w-4 h-4" />}
                  <span className="ml-2">Open Box</span>
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Boxes ({boxes.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Box</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>USN Count</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {!boxes.length && (
                      <TableRow><TableCell colSpan={4} className="text-center text-gray-400 py-6">
                        No boxes yet
                      </TableCell></TableRow>
                    )}
                    {boxes.map((b) => (
                      <TableRow key={b.box_barcode} data-testid={`box-row-${b.box_barcode}`}>
                        <TableCell className="font-mono text-xs">{b.box_barcode}</TableCell>
                        <TableCell><Badge variant="outline" className="text-[10px]">{b.status}</Badge></TableCell>
                        <TableCell>{b.usn_count}</TableCell>
                        <TableCell className="text-right">
                          {b.status === 'OPEN' && (
                            <>
                              <Button size="sm" variant={activeBox === b.box_barcode ? 'default' : 'outline'}
                                onClick={() => setActiveBox(b.box_barcode)}
                                data-testid={`activate-box-${b.box_barcode}`}>
                                {activeBox === b.box_barcode ? 'Scanning' : 'Scan Into'}
                              </Button>
                              <Button size="sm" variant="ghost"
                                onClick={() => closeBox(b.box_barcode)}
                                data-testid={`close-box-${b.box_barcode}`}>
                                <Check className="w-3.5 h-3.5" />
                              </Button>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {activeBox && activeBoxDoc?.status === 'OPEN' && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Scanning into {activeBox}</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={scan} className="flex items-center gap-2">
                    <Scan className="w-4 h-4 text-gray-500" />
                    <Input ref={usnRef} value={usn}
                      onChange={(e) => setUsn(e.target.value)}
                      placeholder="Scan replace USN from jobber return…"
                      className="h-10 max-w-md" disabled={scanning}
                      data-testid="scan-usn-input" />
                    <Button type="submit" disabled={scanning}
                      data-testid="scan-usn-submit">
                      {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Scan'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </main>
    </div>
    </StaffScanGate>
  );
};

export default ShirtsAssortment;
